/**
 * Dynamic loaders for the heavy PDF dependencies.
 *
 * pdf-lib, pdfjs, and JSZip together dominate the bundle, but none of them are
 * needed to render the initial page — only once the user actually picks a file.
 * Loading them on demand keeps them out of the critical path.
 *
 * The tradeoff is that the first interaction would otherwise pay the download
 * cost, so `prefetch*` warms each chunk during idle time. Callers should still
 * `await` the loaders; prefetching only means the promise is likely resolved
 * already. Webpack dedupes concurrent imports of the same chunk, so calling a
 * loader while its prefetch is in flight is safe.
 */

export const loadPdfLib = () =>
  import(/* webpackChunkName: "pdf-lib" */ 'pdf-lib');

export const loadPdfProcessing = () =>
  import(/* webpackChunkName: "pdf-processing" */ './pdfProcessing');

export const loadZipPackager = () =>
  import(/* webpackChunkName: "zip-packager" */ './zipPackager');

export const loadPdfJs = () =>
  import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/webpack');

const whenIdle = (fn) => {
  if (typeof window === 'undefined') {
    return;
  }
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(fn, { timeout: 2000 });
  } else {
    window.setTimeout(fn, 1000);
  }
};

// A rejected prefetch must not surface as an unhandled rejection — the real
// call site will retry and report the failure itself.
const prefetch = (loader) => whenIdle(() => { loader().catch(() => {}); });

/** Warms what's needed the moment a file is selected. */
export const prefetchFileLoadDeps = () => prefetch(loadPdfLib);

/** Warms what's needed when "Create Booklet" is clicked. */
export const prefetchBookletDeps = () => {
  prefetch(loadPdfProcessing);
  prefetch(loadZipPackager);
};
